import { ClockIcon, PlusIcon } from '@heroicons/react/24/solid';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { ModuleRow } from './ModuleRow';
import type {
  ModuleManifest,
  ModuleRef,
  PipelineManifest,
  StageDoc,
} from './types';
import {
  checkCompatibility,
  getModuleRefName,
  toModuleRefObject,
} from './types';

interface StageRowProps {
  stage: StageDoc;
  stageIndex: number;
  isLast: boolean;
  manifest: PipelineManifest;
  onReplaceModule: (moduleIndex: number, next: ModuleRef) => void;
  onAppendModule: (mod: ModuleRef) => void;
  onRemoveModule: (moduleIndex: number) => void;
  /** doc.module_config snapshot — each module reads its own override slice. */
  moduleConfigOverrides: Record<string, Record<string, unknown>>;
  /** Forwarded to ModuleRow's Edit modal. */
  onSetModuleConfig: (
    moduleName: string,
    knobName: string,
    value: string | number | boolean | string[] | null | undefined
  ) => void;
}

const STAGE_SUBTITLES: Record<string, string> = {
  ingest:
    'Extract analyzable units from the target source. Choose CPG (joern) or tree-sitter fallback.',
  detect: 'Run LLM-driven detectors over the ingested units.',
  review: 'Agentic review of detected findings. Discard or refine.',
  skeptic: 'Calibrate severity. Reduce false positives.',
  report: 'Render the final findings.',
  exploit: 'Optional: attempt exploit verification for high-severity findings.',
};

// Stage-specific accent colors. Subtle — used only for the numbered badge
// and a thin left rail, so different stages are skimmable without
// dominating the visual hierarchy.
const STAGE_ACCENTS: Record<string, string> = {
  ingest: 'bg-blue-500/15 text-blue-500',
  detect: 'bg-purple-500/15 text-purple-500',
  review: 'bg-pink-500/15 text-pink-500',
  skeptic: 'bg-amber-500/15 text-amber-500',
  report: 'bg-emerald-500/15 text-emerald-500',
  exploit: 'bg-red-500/15 text-red-500',
};

const DEFAULT_ACCENT =
  'bg-chariot-text-secondary/15 text-chariot-text-secondary';

export function StageRow({
  stage,
  stageIndex,
  isLast,
  manifest,
  onReplaceModule,
  onAppendModule,
  moduleConfigOverrides,
  onSetModuleConfig,
  onRemoveModule,
}: StageRowProps) {
  const stageName = String(stage.name);
  const subtitle = STAGE_SUBTITLES[stageName];
  const accent = STAGE_ACCENTS[stageName] ?? DEFAULT_ACCENT;
  const siblings = stage.modules.map(getModuleRefName);

  // Resolve current sibling manifests for compatibility checks. Falls
  // back to null for unknown siblings (custom-uploaded modules); those
  // contribute no incompatibility constraints (we can't know what they
  // exclude).
  const siblingManifests: ModuleManifest[] = siblings
    .map(n => manifest.modules[n])
    .filter((m): m is ModuleManifest => Boolean(m));

  // Modules eligible for the add-module picker:
  //   - `guard: true` (calibrated, exposable)
  //   - registered for this stage
  //   - not already a sibling
  //   - compatible with EVERY current sibling (constantine's
  //     incompatible_with constraints; symmetric check via
  //     checkCompatibility helper). Saves the user from picking a
  //     combo that constantine's runtime would reject at submit time.
  const stageCandidates = Object.values(manifest.modules).filter(m => {
    if (m.guard === false) return false;
    const stages = Array.isArray(m.stage) ? m.stage : [m.stage];
    if (!stages.includes(stageName as never)) return false;
    if (siblings.includes(m.name)) return false;
    if (checkCompatibility(m, siblingManifests) !== null) return false;
    return true;
  });

  return (
    <div className="relative flex gap-3">
      {/* Numbered badge column with optional connector line to next stage */}
      <div className="flex flex-col items-center gap-2 pt-1">
        <div
          className={`flex size-9 shrink-0 items-center justify-center rounded-md font-mono text-sm font-bold ${accent}`}
          aria-hidden="true"
        >
          {String(stageIndex + 1).padStart(2, '0')}
        </div>
        {!isLast && (
          <div className="bg-chariot-stroke w-px flex-1" aria-hidden="true" />
        )}
      </div>

      {/* Stage body */}
      <div className="border-chariot-stroke flex min-w-0 flex-1 flex-col gap-2.5 rounded-md border p-3 pb-3.5">
        <header className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <h5 className="text-chariot-text-primary text-sm font-semibold tracking-wide uppercase">
                {stageName}
              </h5>
              {stage.timeout && (
                <span className="text-chariot-text-secondary flex items-center gap-1 text-[10px] tracking-wider uppercase">
                  <ClockIcon className="size-3" />
                  {formatTimeout(stage.timeout)}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-chariot-text-secondary text-xs leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          {stageCandidates.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-chariot-text-secondary hover:text-chariot-text-primary h-7 shrink-0 gap-1.5 text-xs"
                  aria-label={`Add module to ${stageName}`}
                >
                  <PlusIcon className="size-3.5" />
                  Add module
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-1">
                <div className="flex flex-col">
                  {stageCandidates.map(candidate => (
                    <button
                      type="button"
                      key={candidate.name}
                      onClick={() => onAppendModule({ name: candidate.name })}
                      className="hover:bg-chariot-background-secondary flex flex-col items-start gap-1 rounded-sm px-3 py-2.5 text-left"
                    >
                      <span className="text-chariot-text-primary text-sm font-semibold">
                        {candidate.display_name}
                      </span>
                      <span className="text-chariot-text-secondary line-clamp-2 text-xs">
                        {candidate.description}
                      </span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </header>

        <div className="flex flex-col gap-1.5">
          {stage.modules.map((mod, moduleIndex) => {
            const modObj = toModuleRefObject(mod);
            return (
              <ModuleRow
                key={`${modObj.name}-${moduleIndex}`}
                moduleRef={modObj}
                manifestEntry={manifest.modules[modObj.name] ?? null}
                manifest={manifest}
                stage={stageName}
                siblings={siblings.filter(n => n !== modObj.name)}
                onSwap={next => onReplaceModule(moduleIndex, next)}
                onRemove={() => onRemoveModule(moduleIndex)}
                configOverrides={moduleConfigOverrides[modObj.name] ?? {}}
                onSetConfig={(knobName, value) =>
                  onSetModuleConfig(modObj.name, knobName, value)
                }
              />
            );
          })}
          {stage.modules.length === 0 && (
            <p className="text-chariot-text-secondary px-1 py-2 text-xs italic">
              No modules in this stage. Click &ldquo;Add module&rdquo; to add
              one.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimeout(seconds: number): string {
  if (seconds >= 3600) {
    const hours = seconds / 3600;
    return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)}h`;
  }
  if (seconds >= 60) return `${Math.round(seconds / 60)}m`;
  return `${seconds}s`;
}
